import type { Option } from '@branchjs/neo-components';
import { createSelector } from 'reselect';

import { EMPTY_ARRAY, type RootState } from 'client/js/redux';
import { INIT_STATE } from './constants';
import type { LinkDataObject, Redirect } from '../../../main/types/links';
import type { QrCodeLinkCreatorState, RedirectValue } from './types';
import type { QrCodeSettings } from '../../../../../shared/types';
import { ANALYTICS_TAGS_WIZARD_TEXT } from 'client/js/main/screens/ScreenQrCodeLinkCreator/constants/AnalyticsTagsWizardText';
import { CUSTOMIZE_QR_CODE_WIZARD_TEXT } from 'client/js/main/screens/ScreenQrCodeLinkCreator/constants/CustomizeQrCodeWizardText';
import { GENERAL_WIZARD_TEXT } from 'client/js/main/screens/ScreenQrCodeLinkCreator/constants/GeneralWizardText';
import { LINK_DATA_WIZARD_TEXT } from 'client/js/main/screens/ScreenQrCodeLinkCreator/constants/LinkDataWizardText';
import { REDIRECTS_WIZARD_TEXT } from 'client/js/main/screens/ScreenQrCodeLinkCreator/constants/RedirectsWizardText';
import { selectQrCodeSettings } from '../AppStore';

export const selectQrCodeLinkCreatorState = (state: RootState): QrCodeLinkCreatorState => {
  return state.qrCodeLinkCreator || INIT_STATE;
};

export const selectEditingQrCodeLinkId = createSelector<RootState, QrCodeLinkCreatorState, string | undefined>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.id;
  }
);

export const selectIsEditing = createSelector<RootState, QrCodeLinkCreatorState, boolean>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return !!qrCodeLinkCreator.id;
  }
);

export const selectStep = createSelector<RootState, QrCodeLinkCreatorState, string>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.step;
  }
);

export const selectHasQrCodeLinkDefineStepVisited = createSelector<RootState, QrCodeLinkCreatorState, boolean>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.defineStepVisited;
  }
);

export const selectHasQrCodeLinkAnalyticTagsStepVisited = createSelector<RootState, QrCodeLinkCreatorState, boolean>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.analyticTagsStepVisited;
  }
);

export const selectHasQrCodeLinkRedirectsStepVisited = createSelector<RootState, QrCodeLinkCreatorState, boolean>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.redirectsStepVisited;
  }
);

export const selectHasQrCodeLinkCustomLinkDataStepVisited = createSelector<RootState, QrCodeLinkCreatorState, boolean>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.customLinkDataStepVisited;
  }
);

export const selectHasQrCodeLinkCustomizeStepVisited = createSelector<RootState, QrCodeLinkCreatorState, boolean>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.customizeStepVisited;
  }
);

export const selectQrCodeLinkHasBlacklistError = createSelector<RootState, QrCodeLinkCreatorState, boolean>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.hasBlacklistError;
  }
);

export const selectQrCodeLinkTitle = createSelector<RootState, QrCodeLinkCreatorState, string>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.title;
  }
);

export const selectQrCodeLinkFeature = createSelector<RootState, QrCodeLinkCreatorState, undefined | Option>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.feature;
  }
);

export const selectQrCodeLinkChannel = createSelector<RootState, QrCodeLinkCreatorState, undefined | Option>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.channel;
  }
);

export const selectQrCodeLinkCampaign = createSelector<RootState, QrCodeLinkCreatorState, undefined | Option>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.campaign;
  }
);

export const selectQrCodeLinkTags = createSelector<RootState, QrCodeLinkCreatorState, Option[]>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.tags || EMPTY_ARRAY;
  }
);

export const selectQrCodeLinkIos = createSelector<RootState, QrCodeLinkCreatorState, RedirectValue>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.ios;
  }
);

export const selectQrCodeLinkIosRedirect = createSelector<RootState, QrCodeLinkCreatorState, Redirect>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.iosRedirect;
  }
);

export const selectQrCodeLinkAndroid = createSelector<RootState, QrCodeLinkCreatorState, RedirectValue>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.android;
  }
);

export const selectQrCodeLinkAndroidRedirect = createSelector<RootState, QrCodeLinkCreatorState, Redirect>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.androidRedirect;
  }
);

export const selectQrCodeLinkIsWebOnly = createSelector<RootState, QrCodeLinkCreatorState, boolean>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.isWebOnly;
  }
);

export const selectQrCodeLinkCustomLinkData = createSelector<RootState, QrCodeLinkCreatorState, LinkDataObject[]>(
  selectQrCodeLinkCreatorState,
  (qrCodeLinkCreator) => {
    return qrCodeLinkCreator.linkData || EMPTY_ARRAY;
  }
);

export const areQrCodeSettingsEqual = (oldValue?: QrCodeSettings, newValue?: QrCodeSettings) => {
  return JSON.stringify(oldValue) === JSON.stringify(newValue);
};

export const selectQrCodeLinkQrCodeCustomization = createSelector<
  RootState,
  QrCodeLinkCreatorState,
  QrCodeSettings | undefined,
  QrCodeSettings
>(selectQrCodeLinkCreatorState, selectQrCodeSettings, (qrCodeLinkCreator, appQrCodeSettings) => {
  return {
    ...appQrCodeSettings,
    ...qrCodeLinkCreator.qrCodeCustomization,
  };
});

export const selectQrCodeLinkDefineCompleted = createSelector<RootState, string, boolean>(selectQrCodeLinkTitle, (title) => {
  const stepCompleted = !!title;

  return stepCompleted;
});

export const selectQrCodeLinkAnalyticTagsCompleted = createSelector<RootState, QrCodeLinkCreatorState, boolean>(
  // @ts-ignore Returns true until fields are required or templates are introduced, the step is always completed
  selectQrCodeLinkCreatorState,
  () => true
);

export const selectQrCodeLinkRedirectsCompleted = createSelector<RootState, QrCodeLinkCreatorState, boolean>(
  // @ts-ignore Returns true until fields are required or templates are introduced, the step is always completed
  selectQrCodeLinkCreatorState,
  () => true
);

export const selectQrCodeLinkCustomLinkDataCompleted = createSelector<RootState, boolean, boolean>(
  selectQrCodeLinkHasBlacklistError,
  (hasBlacklistError) => {
    const stepCompleted = !hasBlacklistError;

    return stepCompleted;
  }
);

export const selectQrCodeLinkCustomizeCompleted = createSelector<RootState, QrCodeLinkCreatorState, boolean>(
  // @ts-ignore Returns true until fields are required or templates are introduced, the step is always completed
  selectQrCodeLinkCreatorState,
  () => true
);

export const selectQrCodeLinkCurrentStepIsCompleted = createSelector<
  RootState,
  string,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean
>(
  selectStep,
  selectQrCodeLinkDefineCompleted,
  selectQrCodeLinkAnalyticTagsCompleted,
  selectQrCodeLinkRedirectsCompleted,
  selectQrCodeLinkCustomLinkDataCompleted,
  selectQrCodeLinkCustomizeCompleted,
  (step, defineCompleted, analyticTagsCompleted, redirectsCompleted, customLinkDataCompleted, customizeCompleted) => {
    if (step === GENERAL_WIZARD_TEXT.step) {
      return defineCompleted;
    }
    if (step === ANALYTICS_TAGS_WIZARD_TEXT.step) {
      return analyticTagsCompleted;
    }
    if (step === REDIRECTS_WIZARD_TEXT.step) {
      return redirectsCompleted;
    }
    if (step === LINK_DATA_WIZARD_TEXT.step) {
      return customLinkDataCompleted;
    }
    if (step === CUSTOMIZE_QR_CODE_WIZARD_TEXT.step) {
      return customizeCompleted;
    }
    return false;
  }
);

export const selectQrCodeLinkWizardAllStepsCompleted = createSelector<
  RootState,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean
>(
  selectQrCodeLinkDefineCompleted,
  selectQrCodeLinkAnalyticTagsCompleted,
  selectQrCodeLinkRedirectsCompleted,
  selectQrCodeLinkCustomLinkDataCompleted,
  selectQrCodeLinkCustomizeCompleted,
  (defineCompleted, analyticTagsCompleted, redirectsCompleted, customLinkDataCompleted, customizeCompleted) => {
    return defineCompleted && analyticTagsCompleted && redirectsCompleted && customLinkDataCompleted && customizeCompleted;
  }
);
